import {
  documentQuery,
  sbEvaluationReportQuery,
  useAcceptSbEvalReportMutation,
  useFinalizeSbEvaluationFieldMutation,
  useUnfinalizeSbEvaluationFieldMutation,
  useUpdateSbEvaluationFieldMutation,
} from "@common/api"
import { Form } from "@common/components/ui/form"
import { useSharedForm } from "@common/hooks/use-shared-form"
import { handleError } from "@common/utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import useCustomToast from "@/hooks/use-custom-toast"
import { AcceptAssessmentBar } from "../-components/accept-assessment-bar"
import {
  AssessmentDocumentPanel,
  AssessmentPanels,
  type ViewerFocus,
} from "../-components/assessment-panels"
import { SectionCard } from "../-components/section-card"
import {
  mapSbReportToForm,
  type SbAssessmentInput,
  sbAssessmentSchema,
} from "../-lib/schema"
import { resolveSbSections, type SbFieldName } from "../-lib/sections"

export const Route = createFileRoute("/_layout/assessments/sb/$reportId")({
  loader: async ({ context: { queryClient }, params: { reportId } }) => {
    const report = await queryClient.ensureQueryData(
      sbEvaluationReportQuery({ id: reportId }),
    )
    if (!report.document_id) throw notFound()
    const document = await queryClient.ensureQueryData(
      documentQuery({ document_id: report.document_id }),
    )
    return { breadcrumb: `SB assessment: ${document.file_name}` }
  },
  component: SbAssessmentPage,
})

function SbAssessmentPage(): React.ReactElement | null {
  const { reportId } = Route.useParams()
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const { data: report } = useSuspenseQuery(
    sbEvaluationReportQuery({ id: reportId }),
  )
  const [focus, setFocus] = useState<ViewerFocus | null>(null)

  const form = useSharedForm<SbAssessmentInput>({
    resolver: zodResolver(sbAssessmentSchema),
    values: mapSbReportToForm(report),
    resetOptions: { keepDirtyValues: true },
    mode: "onBlur",
    criteriaMode: "all",
  })

  const updateField = useUpdateSbEvaluationFieldMutation()
  const finalizeField = useFinalizeSbEvaluationFieldMutation()
  const unfinalizeField = useUnfinalizeSbEvaluationFieldMutation()
  const acceptReport = useAcceptSbEvalReportMutation()

  const documentId = report.document_id
  if (!documentId) return null

  const sections = resolveSbSections(report)
  const finalisedCount = sections.filter((section) => section.finalised).length
  const acceptSection = async (name: SbFieldName) => {
    if (!(await form.trigger(name))) return
    const persisted = form.getFieldState(name).isDirty
      ? updateField.mutateAsync({
          report_id: reportId,
          field_name: name,
          sbFieldUpdateRequest: { edited_value: form.getValues(name) },
        })
      : Promise.resolve(null)
    return persisted
      .then(() =>
        finalizeField.mutateAsync({ report_id: reportId, field_name: name }),
      )
      .catch((error) => handleError(error, showToast))
  }

  const reopenSection = (name: SbFieldName) =>
    unfinalizeField.mutate(
      { report_id: reportId, field_name: name },
      { onError: (error) => handleError(error, showToast) },
    )

  const acceptAssessment = () =>
    acceptReport.mutate(
      { acceptedSbAssessmentCreate: { trace_sb_eval_report_id: reportId } },
      {
        onSuccess: (accepted) => {
          showToast("Success", "Assessment accepted", "success")
          navigate({
            to: "/aircraft/$aircraftId",
            params: { aircraftId: String(accepted.aircraft_id) },
          })
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <Form {...form}>
      <AssessmentPanels
        viewer={
          <AssessmentDocumentPanel
            focus={focus}
            subjectDocumentId={documentId}
          />
        }
        form={
          <>
            <div className="flex flex-col gap-3 p-4">
              {sections.map((section) => (
                <SectionCard<SbAssessmentInput>
                  key={section.name}
                  section={section}
                  dirty={!!form.formState.dirtyFields[section.name]}
                  subjectDocumentId={documentId}
                  onAccept={() => acceptSection(section.name)}
                  onReopen={() => reopenSection(section.name)}
                  onShowReference={(reference) =>
                    setFocus({
                      documentId: reference.document_id,
                      page: reference.page,
                      evidence: [reference.text],
                    })
                  }
                />
              ))}
            </div>
            <AcceptAssessmentBar
              finalisedCount={finalisedCount}
              total={sections.length}
              hasAircraft={report.aircraft_id != null}
              pending={acceptReport.isPending}
              onAccept={acceptAssessment}
            />
          </>
        }
      />
    </Form>
  )
}
